import axios from "axios";

// Public, no-login storefront. Every /storefront-api/* call carries this
// seller's own credentials as headers — never a URL param — set once, per
// deployment, in .env: REACT_APP_SELLER_ID (this seller's MetaAccount id,
// what used to be the {{seller_id}} URL segment) and
// REACT_APP_STOREFRONT_API_KEY (generated automatically server-side the
// first time this seller opens Settings → Web Order — see
// SettingsService.ensureWebOrderRow / StorefrontApiKeyGuard).
export const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:9000";
const SELLER_ID = process.env.REACT_APP_SELLER_ID;
const API_KEY = process.env.REACT_APP_STOREFRONT_API_KEY;

// Order page brand color: active sidebar category, back button, timing icons.
export const BRAND_COLOR = process.env.REACT_APP_STORE_BRAND_COLOR || "#520400";

const client = axios.create({
  baseURL: `${API_BASE}/storefront-api`,
  headers: {
    "x-seller-id": SELLER_ID,
    "x-storefront-api-key": API_KEY,
  },
  // Needed both ways for the returning-customer cookie (see resolvePhone/
  // resolveSessionCookie below): lets the browser accept the Set-Cookie the
  // backend sends back, and lets it attach that cookie again on later
  // requests — neither happens cross-origin without this, even though the
  // storefront's own domain and the API's are same-site in production (see
  // storefront-api.controller.ts's cookie comment).
  withCredentials: true,
});

export const resolveUrl = (path) => (path ? (path.startsWith("http") ? path : `${API_BASE}${path}`) : null);

// No session/phone needed — used both for the phone-entry gate screen
// (before we know who the customer is) and, once resolved, as the one
// storefront-branding source for the rest of the page. Which seller this is
// for comes entirely from the headers above, not an argument.
export function getStorefrontBrand() {
  return client.get("/store").then(({ data }) => data.data);
}

// Turns this seller's customer phone into a usable sessionId — reuses an
// existing session for that phone if one already exists (e.g. a prior
// WhatsApp conversation), otherwise bootstraps a new one. Returns
// { success, message?, sessionId? }.
export function resolvePhone(phone) {
  return client.post("/resolve-phone", { phone }).then(({ data }) => data);
}

// Checked once on page load ONLY when there's no ?cp= phone in the URL (a
// direct/ad link) — silently asks the backend "has this browser already
// been recognized before?" via the 365-day cookie resolve-phone sets above.
// Same { success, sessionId? } shape as resolvePhone, so WebOrderPage.jsx
// treats a cookie-recognized visitor identically to a WhatsApp-link one.
// Never throws — a failure here just means proceed as a fresh anonymous
// visitor, same as if there'd been no cookie at all.
export function resolveSessionCookie() {
  return client.get("/resolve-session-cookie").then(({ data }) => data).catch(() => ({ success: false }));
}

// No sessionId needed — collections/products are resolved directly from the
// header-authenticated seller, same as getStorefrontBrand above.
export function getCollections() {
  return client.get("/collections").then(({ data }) => data.data);
}

export function getProducts({ search } = {}) {
  return client.get("/products", { params: { search } }).then(({ data }) => data.data);
}

// sessionId is only passed when the phone was already known (?cp= in the
// URL) — omit it (undefined, which JSON.stringify just drops) for a direct-
// link checkout with no session yet; the backend handles both via
// prepareWebCheckout vs prepareAnonymousWebCheckout.
// scheduledFor: the delivery slot picked on Checkout outside delivery
// hours (see ScheduleDeliveryPicker) — undefined for a normal order.
export function checkout(sessionId, { items, note, scheduledFor }) {
  return client.post("/checkout", { sessionId, items, note, scheduledFor }).then(({ data }) => data);
}

// Fire-and-forget analytics — a tracking call failing (network hiccup,
// ad-blocker) must never interrupt or error out the shopping experience, so
// both swallow their own errors instead of the caller having to worry about
// it. sessionId is optional on both — see the DTOs on the server
// (TrackPageViewDto/TrackAddToCartDto) for why: a visitor who hasn't been
// phone-resolved yet still gets counted, just without a customer attached.
export function trackPageView(sessionId) {
  return client.post("/track/page-view", { sessionId }).catch(() => {});
}

export function trackAddToCart(sessionId, { productId, productName, productPrice, quantity }) {
  return client.post("/track/add-to-cart", { sessionId, productId, productName, productPrice, quantity }).catch(() => {});
}

// Fired whenever the cart changes at all — add, remove, or quantity edit —
// with the FULL current cart, never just a delta. Backs the seller's Live
// Chat "Cart" indicator, which always shows what's actually in the cart
// right now rather than a running history of every add that ever happened.
export function trackCartUpdated(sessionId, items) {
  return client.post("/track/cart-updated", { sessionId, items }).catch(() => {});
}

// Fired every time the cart/checkout screen opens — no dedup, unlike
// trackPageView (which is deduped per browsing session client-side via
// sessionStorage before it's ever called). Opening it three times in one
// visit records three separate rows on the seller's Order Activity timeline.
export function trackCartViewed(sessionId) {
  return client.post("/track/cart-viewed", { sessionId }).catch(() => {});
}

// Fired once a product's quantity is actually brought down to zero (minus
// button, or an explicit remove) — never for a quantity edit that stays
// above zero. Same one-row-per-action shape as trackAddToCart, so the
// seller's Order Activity timeline can show each removal on its own instead
// of inferring it from trackCartUpdated's cart snapshots shrinking.
export function trackRemovedFromCart(sessionId, { productId, productName, productPrice, quantity }) {
  return client.post("/track/removed-from-cart", { sessionId, productId, productName, productPrice, quantity }).catch(() => {});
}

export function formatPrice(price) {
  return `₹${Number(price || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// The single rule for "what does this product/variant actually cost" —
// mirrors the backend's resolveEffectivePrice (server/src/common/currency.util.ts)
// exactly, so what's displayed here always matches what checkout actually
// charges. Sale Price wins only when it's a genuine positive amount — unset,
// null, or 0 all fall back to Price, same as the backend.
export function resolveEffectivePrice(price, salePrice) {
  const sale = Number(salePrice);
  return Number.isFinite(sale) && sale > 0 ? sale : Number(price) || 0;
}
